import React from 'react'
import { Set } from 'immutable'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'

import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'

import { Button as ChromeButton, Dialog } from 'src/chrome'
import { CampaignLineFragment } from 'src/graphql'
import { useEffect, useState } from 'src/hooks'

import styles from './createOrderModal.module.scss'
import { Div, Text } from '@sendoutcards/quantum-design-ui'

interface Props {
  lines: CampaignLineFragment[]
  onCreateCampaign: (
    lines: CampaignLineFragment[],
    campaignName: string,
  ) => void
  onSubmit: (lines: CampaignLineFragment[] | null, campaignName: string) => void
  onClose: () => void
  isOpen: boolean
}

const Hooks = (props: Props) => {
  const { onClose, lines, onSubmit, onCreateCampaign } = props

  const [campaignName, setCampaignName] = useState('')
  const [
    isCreateCampaignOverlayOpen,
    setIsCreateCampaignOverlayOpen,
  ] = useState(false)
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false)
  const [selected, setSelected] = useState(Set())

  const handleChange = (index: number) => {
    const newSet = selected.has(index)
      ? selected.delete(index)
      : selected.add(index)
    setSelected(newSet)
    setIsSelectAllChecked(newSet.size === lines.length)
  }

  const handleClose = () => {
    setIsSelectAllChecked(false)
    setSelected(Set())
    onClose()
  }

  const handleCreateCampaign = () => {
    const selectedLines: CampaignLineFragment[] = selected
      .toArray()
      .map(value => lines[value])
    onCreateCampaign(selectedLines, campaignName)
  }

  const handleSelectAll = () => {
    setIsSelectAllChecked(!isSelectAllChecked)
  }

  useEffect(() => {
    // After 'isSelectAllChecked' state is changed, we need to set all the selected cards appropriately
    setSelected(
      Set(isSelectAllChecked ? lines.map((line, index) => index) : []),
    )
  }, [isSelectAllChecked, lines])

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault()

    const selectedLines =
      selected.size === lines.length
        ? null
        : selected.toArray().map(value => lines[value])
    onSubmit(selectedLines, campaignName)
  }

  const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCampaignName(event.currentTarget.value)
  }

  const showOverlay = () => {
    setIsCreateCampaignOverlayOpen(!isCreateCampaignOverlayOpen)
  }

  return {
    campaignName,
    isCreateCampaignOverlayOpen,
    handleChange,
    handleClose,
    handleCreateCampaign,
    handleSelectAll,
    handleSubmit,
    handleTextInput,
    isSelectAllChecked,
    selected,
    showOverlay,
  }
}

const CreateOrderModal: React.FC<Props> = props => {
  const { onClose, lines, isOpen } = props

  const {
    campaignName,
    isCreateCampaignOverlayOpen,
    handleChange,
    handleClose,
    handleCreateCampaign,
    handleSelectAll,
    handleSubmit,
    handleTextInput,
    isSelectAllChecked,
    selected,
    showOverlay,
  } = Hooks(props)

  return (
    <Dialog open={isOpen} onClose={onClose} scroll={'paper'}>
      <DialogTitle disableTypography={true}>
        <Div display="flex">
          <Text type="caption">Select items: </Text>
          <Text type="caption" weight="bold" color="primaryHeading">
            {selected.size} selected
          </Text>
        </Div>
      </DialogTitle>
      <DialogContent
        style={{ position: 'relative' }}
        className={styles.content}
      >
        <div className={styles.delayForm}>
          <form onSubmit={handleSubmit} className={styles.formContainer}>
            <div className={styles.linesContainer} id={'tour-linesContainer'}>
              {lines.map((line, index) => {
                return (
                  <div
                    key={index}
                    className={
                      line.card?.isHorizontal
                        ? `${styles.tileContainer} ${styles.horizontal}`
                        : styles.tileContainer
                    }
                    onClick={() => handleChange(index)}
                  >
                    <div
                      className={
                        line.card?.isHorizontal
                          ? `${styles.card} ${styles.horizontal}`
                          : styles.card
                      }
                      style={
                        selected.has(index)
                          ? {
                              border: '2px solid #ff5689',
                              backgroundImage: `url(${
                                line.card?.frontPreviewUrl ?? ''
                              })`,
                            }
                          : {
                              backgroundImage: `url(${
                                line.card?.frontPreviewUrl ?? ''
                              })`,
                            }
                      }
                    />
                    {line.giftVariation && (
                      <div
                        className={styles.gift}
                        style={{
                          backgroundImage: `url(${line.giftVariation.defaultImageUrl})`,
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
            <div className={styles.modalFooter}>
              <div className={styles.actionButton}>
                <FormControlLabel
                  control={
                    <Checkbox
                      id={'select_all_btn'}
                      checked={isSelectAllChecked}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Select All"
                />
              </div>
            </div>
          </form>
        </div>
        {isCreateCampaignOverlayOpen && (
          <div className={styles.overlay}>
            <div>
              <Text type="body">Name your new campaign</Text>
              <TextField
                label={'Name'}
                InputLabelProps={{
                  classes: {
                    root: styles.nameInputLabel,
                  },
                }}
                InputProps={{
                  classes: {
                    root: styles.nameInput,
                    underline: styles.disabledNameInput,
                  },
                }}
                value={campaignName}
                onChange={handleTextInput}
              />
              <div style={{ display: 'flex', paddingTop: 10 }}>
                <ChromeButton
                  title={'Close'}
                  onClick={showOverlay}
                  style={{ marginRight: 10, width: 100 }}
                />
                <ChromeButton
                  title={'Submit'}
                  buttonColor={'pink'}
                  disabled={campaignName.length < 1}
                  onClick={handleCreateCampaign}
                />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={'cancel_order_btn'} // classNames are for selenium testing
          key={'Cancel create order'}
          color={'secondary'}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          className={'create_campaign_btn'}
          key={'Create Campaign Button'}
          color={'primary'}
          disabled={selected.size < 1}
          onClick={showOverlay}
        >
          New Campaign
        </Button>
        <Button
          className={'submit_selected_order_btn'}
          key={'Submit created order'}
          color={'primary'}
          disabled={selected.size < 1 || isCreateCampaignOverlayOpen}
          onClick={handleSubmit}
          id="submit_order_btn"
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateOrderModal
